import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useTranslation } from '~/behaviors';
import { accountActions } from '~/behaviors/actions/account.actions';
import { jss } from '~/tools/jss';
import { StatusDialog, Loading } from '../UI.shared';
import { urls } from '~/tools/urls';

const useStyles = jss.makeStyles(() => ({
  container: {
    height: 200,
  },
}));

const defaultDialogState = {
  status: 'info',
  title: '',
  message: '',
  isVisible: false,
  closeButtonLabel: '',
};

const SSOPage = () => {
  // ------------------------------------------------------------------ State --
  const dispatch = useAppDispatch();
  const styles = useStyles(undefined);
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(defaultDialogState);
  const { t } = useTranslation();
  const location = useLocation();
  const [kazooLoginUrl, setKazooLoginUrl] = useState(null);

  const searchParams = urls.parseQueryString(location.search);
  const hashParams = urls.parseQueryString(location.hash.slice(1));
  const params = { ...searchParams, ...hashParams };
  const clientId = params?.client_id;
  const idToken = params?.id_token;

  // -------------------------------------------------------------- Lifecycle --
  useEffect(() => {
    if (!clientId || !idToken) return;

    dispatch(
      accountActions.loginWithSSO({
        clientId,
        ssoToken: idToken,
      }),
    ).then((action) => {
      if (action.error) {
        const error = action.payload;
        if (error.code === 'PULLGN0001') {
          setKazooLoginUrl(error?.customObj?.kazooLoginUrl);
          return setDialog({
            status: 'error',
            title: t('sso.loginFailed'),
            message: t('sso.loginFailed.invalidToken'),
            closeButtonLabel: t('button.back'),
            isVisible: true,
          });
        }
        setDialog({
          status: 'error',
          title: t('user.not.found'),
          message: t('sso.user.not.found.description', { email: error?.customObj?.email }),
          closeButtonLabel: t('back.to.login'),
          isVisible: true,
        });
      }
    });
  }, [clientId, idToken, dispatch, t]);

  // ----------------------------------------------------------------- Events --
  const handleDialogClose = () => {
    if (kazooLoginUrl) {
      window.location.href = kazooLoginUrl;
      return;
    }
    navigate('/login');
  };

  // -------------------------------------------------------------- Interface --
  if (!clientId || !idToken) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div data-testid="sso-page">
      <StatusDialog
        status={dialog.status}
        title={dialog.title}
        open={dialog.isVisible}
        onClose={handleDialogClose}
        closeButtonLabel={dialog.closeButtonLabel}
      >
        {dialog.message}
      </StatusDialog>

      <div className={styles.container}>
        <Loading />
      </div>
    </div>
  );
};

export default SSOPage;
